import { computed, nextTick, reactive } from 'vue';
import type { ToastPosition, ToastTheme } from '@ui-components/UIToast';

export interface ToastActions {
  dismiss: () => void;
  present: () => void;
}

export interface ToastOptions {
  theme?: ToastTheme;
  position?: ToastPosition;
  title?: string;
  message: string;
  duration?: number;
  dismissible?: boolean;
  onClose?: () => void;
  onClick?: () => void;
}

export interface Toast extends ToastActions{
  id: string;
  options: ToastOptions;
  visible: boolean;
  timeout?: number;
  destroy: () => void;
}

interface State {
  toasts: Map<string, Toast>;
}

const state = reactive<State>({
  toasts: new Map(),
})

const defaultOptions: Omit<ToastOptions, 'message'|'title'> = {
  theme: 'info',
  position: 'bottom',
  dismissible: true,
  duration: 5000,
}

const present = (id: string): void => {
  const toast = state.toasts.get(id);

  if (!toast) throw new Error('Toast does not exist anymore.');

  toast.visible = true;

  if (toast.options.duration) {
    toast.timeout = window.setTimeout(toast.dismiss, toast.options.duration);
  }
}

const dismiss = (id: string): void => {
  const toast = state.toasts.get(id);

  if (!toast) throw new Error('Toast does not exist anymore.');

  if (toast.timeout) window.clearTimeout(toast.timeout);

  if (toast.options.onClose) toast.options.onClose();

  toast.visible = false;
}

const destroy = (id: string): void => {
  const toast = state.toasts.get(id);

  if (!toast) throw new Error('Toast does not exist anymore.');

  state.toasts.delete(id);
}

export const useToast = async (options: ToastOptions): Promise<ToastActions> => {
  const id = randomString(16);

  const toast: Toast = {
    id,
    options: {...defaultOptions, ...options},
    present: () => present(id),
    dismiss: () => dismiss(id),
    destroy: () => destroy(id),
    visible: false,
  }

  state.toasts.set(id, toast);

  await nextTick();

  return {present: toast.present, dismiss: toast.dismiss};
}

export const useToasts = () => computed(() => state.toasts);
